import { call, delay, put, select, takeLatest } from 'redux-saga/effects';

import {
  dbConfigurationsSlice,
  selectConfigurationParameterById,
} from '!core/redux';
import { onboardingSlice } from '»components/Auth';
import { disclaimersServices } from '»components/molecules/TrlDisclaimer';
import { questionnaireSlice } from '»components/Questionnaire/redux';
import { isStinvestActiveClient } from '»utils/clientsUtils';

import {
  legalStatusSelector,
  onboardingBankAccountNumberSelector,
  onboardingIbanCodeSelector,
  onboardingSecondaryIbanCodeSelector,
  onboardingShouldShowQuestionnaireSelector,
} from './onboarding.selectors';
import { onboardingServices } from './onboarding.services';

const getClassifDescriptionsWorker = function* ({ payload }) {
  try {
    const classifDescriptions = yield call(
      onboardingServices.getClassifDescriptions,
      { classifierId: payload?.classifierId },
    );

    yield put(
      onboardingSlice.actions.getClassifDescriptionsSuccess(
        classifDescriptions,
      ),
    );
    yield put(
      onboardingSlice.actions.setOnboardingCurrentStep({
        direction: 'forward',
      }),
    );
  } catch (ex) {
    yield put(onboardingSlice.actions.getClassifDescriptionsFailure(ex));
  }
};

const handleUserAccountInfo = function* ({
  apiResponse,
  openingRequestId,
  legalStatus,
  answeredSurvey,
  mainPersonHolderCode,
  ibanCode,
  ibanCode2,
  bankAccountNumber,
  disableFormSubmittingState,
}) {
  const questionnaireParameter = yield select((state) =>
    selectConfigurationParameterById(state, 'QUESTIONNAIRE'),
  );

  const hasQuestionnaire = questionnaireParameter.value === 'Y';

  const formPreviousInfo = Object.values(
    apiResponse.reduce((acc, item) => {
      const { holderCode, type } = item;

      if (!acc[holderCode])
        acc[holderCode] = { holderCode, isMainPerson: false };

      if (
        type === 'Account' ||
        type === 'Holder' ||
        type === 'Address' ||
        type === 'Document'
      ) {
        Object.entries(item).forEach(([key, value]) => {
          if (!['type', 'id'].includes(key)) {
            if (type === 'Address' || type === 'Document') {
              acc[holderCode][type] = acc[holderCode][type] || {};
              acc[holderCode][type][key] = value;
            } else {
              acc[holderCode][key] = value;
            }
          }
        });

        if (type === 'Account') acc[holderCode].isMainPerson = true;
      } else {
        if (!acc[holderCode][type]) acc[holderCode][type] = [];

        acc[holderCode][type].push(item);
      }

      return acc;
    }, {}),
  );

  yield put(
    onboardingSlice.actions.getNewAccountInfoSuccess({
      openingRequestId,
      legalStatus,
      ibanCode,
      ibanCode2,
      bankAccountNumber,
      answeredSurvey,
      holderCode: mainPersonHolderCode,
      hasQuestionnaire,
    }),
  );
  yield put(
    dbConfigurationsSlice.actions.dbConfigurationsRequest({
      parameterId: `CR_CHNN_ONBOARD_${legalStatus}`,
      noAuth: true,
      formPreviousInfo,
      iban: ibanCode,
      iban2: ibanCode2,
    }),
  );
  yield put(
    onboardingSlice.actions.getDocumentsListRequest({
      openingRequestId,
      disableFormSubmittingState,
    }),
  );
  if (answeredSurvey === 'N' && hasQuestionnaire)
    yield put(
      questionnaireSlice.actions.getQuestionnaireRequest({
        openingRequestId,
        disableFormSubmittingState,
      }),
    );

  if (!isStinvestActiveClient) {
    const disclaimerResponse = yield call(
      disclaimersServices.getDisclaimerNoAuth,
      { context: 'ONBOARDING' },
    );
    yield put(
      onboardingSlice.actions.setContractDisclaimer({
        disclaimer: disclaimerResponse[0].checkboxNote,
      }),
    );
  }
};

const getNewAccountWorker = function* (action) {
  try {
    const {
      taxId,
      ibanCode,
      bankCode,
      bankAccountNumber,
      dataAccessAgreement,
      disableFormSubmittingState,
      hasOnboardingInitialOtp,
      idType,
      idNumber,
      ibanCode2,
      email1,
    } = action.payload;

    const legalStatus = yield select(legalStatusSelector);

    const response = yield call(onboardingServices.getNewAccountRequest, {
      taxId,
      ibanCode,
      bankCode,
      bankAccountNumber,
      dataAccessAgreement,
      documentCode: idType,
      docNumber: idNumber,
      email: email1,
      ibanCode2: ibanCode2,
      legalStatus,
      hasInitialOtpValidation: hasOnboardingInitialOtp,
    });

    const {
      openingRequestId,
      legalStatus: responseLegalStatus,
      answeredSurvey,
      holderCode: mainPersonHolderCode,
      otpDeliveryMethods,
    } = response[0];

    const currentLegalStatus = responseLegalStatus ?? legalStatus;

    if (hasOnboardingInitialOtp) {
      yield put(
        onboardingSlice.actions.getNewAccountInfoSuccess({
          openingRequestId,
          ibanCode,
          ibanCode2,
          bankAccountNumber,
          userInitialOtpDelivMetds: otpDeliveryMethods,
          hasOnboardingInitialOtp,
        }),
      );

      yield put(
        onboardingSlice.actions.setOnboardingCurrentStep({
          direction: 'forward',
          hasOnboardingInitialOtp,
        }),
      );

      return;
    }

    yield call(handleUserAccountInfo, {
      apiResponse: response,
      openingRequestId,
      legalStatus: currentLegalStatus,
      answeredSurvey,
      mainPersonHolderCode,
      ibanCode,
      ibanCode2,
      bankAccountNumber,
      disableFormSubmittingState,
    });
  } catch (ex) {
    const { disableFormSubmittingState } = action.payload;

    yield put(onboardingSlice.actions.getNewAccountInfoFailure(ex));
    disableFormSubmittingState();
  }
};

const getNewAccountInfoWorker = function* ({
  openingRequestId,
  disableFormSubmittingState,
}) {
  try {
    const ibanCode = yield select(onboardingIbanCodeSelector);
    const ibanCode2 = yield select(onboardingSecondaryIbanCodeSelector);
    const bankAccountNumber = yield select(onboardingBankAccountNumberSelector);

    const response = yield call(onboardingServices.getNewAccountInfo, {
      openingRequestId,
    });

    const {
      legalStatus,
      answeredSurvey,
      holderCode: mainPersonHolderCode,
    } = response[0];

    yield call(handleUserAccountInfo, {
      apiResponse: response,
      openingRequestId,
      legalStatus,
      answeredSurvey,
      mainPersonHolderCode,
      ibanCode,
      ibanCode2,
      bankAccountNumber,
      disableFormSubmittingState,
    });
  } catch (ex) {
    yield put(onboardingSlice.actions.getNewAccountInfoFailure(ex));
    disableFormSubmittingState();
  }
};

const getDocumentsListWorker = function* (action) {
  try {
    const { openingRequestId } = action.payload;

    const documents = yield call(onboardingServices.getDocumentsList, {
      openingRequestId,
    });

    const newDocuments = documents.sort((a, b) => {
      if (a.identification === 'S' && b.identification !== 'S') return -1;

      if (a.identification !== 'S' && b.identification === 'S') return 1;

      if (a.mandatory === 'S' && b.mandatory !== 'S') return -1;

      if (a.mandatory !== 'S' && b.mandatory === 'S') return 1;

      return 0;
    });

    const hasDocumentMandatoryAndIdentification = newDocuments.some(
      (doc) => doc.mandatory === 'S' && doc.identification === 'S',
    );

    yield put(
      onboardingSlice.actions.getDocumentsListSuccess({
        documents: newDocuments,
        hasDocumentMandatoryAndIdentification,
      }),
    );
  } catch (ex) {
    const { disableFormSubmittingState } = action.payload;

    yield put(onboardingSlice.actions.getDocumentsListFailure(ex));
    disableFormSubmittingState();
  }
};

const submitFinancialContractWorker = function* (action) {
  try {
    const shouldShowQuestionnaire = yield select(
      onboardingShouldShowQuestionnaireSelector,
    );

    const { openingRequestId, formValues } = action.payload;

    const completedFormValues = {
      ...formValues,
      otpEmail: formValues.email || formValues.email1,
      otpPhoneNum: formValues.phoneNum,
      ...(isStinvestActiveClient && {
        idType: formValues.idType === 'OUTRO' ? '' : formValues.idType,
        incomeSource: formValues.incomeSource.filter(
          (value) => value !== 'OUTRO',
        ),
      }),
    };

    yield call(onboardingServices.submitFinancialContract, {
      openingRequestId,
      formValues: completedFormValues,
    });

    yield put(onboardingSlice.actions.setMainFormValues(formValues));

    if (!shouldShowQuestionnaire)
      yield put(
        onboardingSlice.actions.generateContractPdfRequest({
          openingRequestId,
        }),
      );

    yield put(
      onboardingSlice.actions.setOnboardingCurrentStep({
        direction: 'forward',
      }),
    );
  } catch (ex) {
    const { disableFormSubmittingState } = action.payload;

    yield put(onboardingSlice.actions.submitFinancialContractFailure(ex));
    disableFormSubmittingState();
  }
};

const generateContractPdfWorker = function* ({ payload }) {
  try {
    const contractResponse = yield call(
      onboardingServices.generateContractPdf,
      {
        openingRequestId: payload.openingRequestId,
      },
    );

    yield put(
      onboardingSlice.actions.generateContractPdfSuccess({
        file: contractResponse.data,
      }),
    );
  } catch (ex) {
    yield put(onboardingSlice.actions.generateContractPdfFailure(ex));

    yield delay(4500);

    yield put(
      onboardingSlice.actions.setOnboardingCurrentStep({
        direction: 'backward',
      }),
    );
  }
};

export const submitDocumentWorker = function* (action) {
  try {
    const {
      deleteDoc,
      docNumber,
      documentCode,
      expirationDate,
      lifetime,
      openingRequestId,
      index,
      file,
      noteId,
      setFieldValue,
      webDescription,
      description,
      identification,
      mandatory,
    } = action.payload;

    let newNoteId = noteId;
    if (!noteId) {
      const response = yield call(onboardingServices.uploadNoteAttach, {
        file,
        description: webDescription,
        fileKey: description,
      });

      newNoteId = response[0].refNoteId;
    }
    yield call(onboardingServices.submitDocument, {
      deleteDoc,
      docNumber,
      documentCode,
      expirationDate,
      lifetime,
      noteId: newNoteId,
      openingRequestId,
    });

    yield put(
      onboardingSlice.actions.submitDocumentSuccess({
        values: {
          docNumber,
          expirationDate,
          lifetime,
          noteId: newNoteId,
          identification,
          mandatory,
          filename: file?.name,
          file,
          validated: deleteDoc === 'N',
        },
        fieldName: description,
        index,
        deleteDoc,
      }),
    );
    setFieldValue(`${documentCode}.validated`, deleteDoc === 'N');
  } catch (error) {
    const { file, description } = action.payload;
    yield put(
      onboardingSlice.actions.submitDocumentFailure({
        error,
        filename: file?.name,
        fieldName: description,
      }),
    );
  }
};

const setOnboardingStateWorker = function* ({ payload }) {
  try {
    const {
      openingRequestId,
      state,
      validationCode,
      disableFormSubmittingState,
    } = payload;

    yield call(onboardingServices.setOnboardingState, {
      openingRequestId,
      state,
      validationCode,
    });

    const hasInitialOtpValidation = state === 'INITIALOK';

    yield put(
      onboardingSlice.actions.setOnboardingStateSuccess({
        hasInitialOtpValidation,
      }),
    );

    if (hasInitialOtpValidation)
      yield call(getNewAccountInfoWorker, {
        openingRequestId,
        disableFormSubmittingState,
      });
  } catch (ex) {
    const { disableFormSubmittingState } = payload;

    yield put(onboardingSlice.actions.setOnboardingStateFailure(ex));
    disableFormSubmittingState();
  }
};

const getLegalStatusListWorker = function* () {
  try {
    const legalStatusList = yield call(onboardingServices.getLegalStatusList);

    yield put(
      onboardingSlice.actions.getLegalStatusListSuccess({
        legalStatusList,
      }),
    );
  } catch (error) {
    yield put(onboardingSlice.actions.getLegalStatusListFailure(error));
  }
};

export const onboardingSagas = function* () {
  yield takeLatest(
    onboardingSlice.actions.getNewAccountRequest.type,
    getNewAccountWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.getClassifDescriptionsRequest.type,
    getClassifDescriptionsWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.getDocumentsListRequest.type,
    getDocumentsListWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.submitFinancialContractRequest.type,
    submitFinancialContractWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.generateContractPdfRequest.type,
    generateContractPdfWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.submitDocumentRequest.type,
    submitDocumentWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.setOnboardingStateRequest.type,
    setOnboardingStateWorker,
  );
  yield takeLatest(
    onboardingSlice.actions.getLegalStatusListRequest.type,
    getLegalStatusListWorker,
  );
};
